import React, { useId, Children, isValidElement, cloneElement } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  resolveSelectPlaceholder,
  isNonSelectableSelectPlaceholder,
  isEmptySelectValue,
  SELECT_HINT_OPTION_STYLE,
} from '../utils/formPlaceholders';

function hintOptionProps(label) {
  if (!isNonSelectableSelectPlaceholder(label)) return {};
  return {
    disabled: true,
    style: SELECT_HINT_OPTION_STYLE,
  };
}

function enhanceOptionChild(child) {
  if (!isValidElement(child) || child.type !== 'option') return child;
  const { value, children, style } = child.props;
  const text = typeof children === 'string' ? children : '';
  if (String(value) !== '' && !isNonSelectableSelectPlaceholder(text)) return child;
  if (!isNonSelectableSelectPlaceholder(text)) return child;
  return cloneElement(child, {
    disabled: true,
    style: { ...style, ...SELECT_HINT_OPTION_STYLE },
  });
}

const Select = ({
  label,
  name,
  value,
  onChange,
  options = [],
  error = '',
  required = false,
  disabled = false,
  placeholder,
  className = '',
  children,
  ...props
}) => {
  const fieldId = useId();
  const selectId = name || fieldId;
  const emptyOption = options.find((o) => String(o.value) === '');
  const skipEmptyDedup = placeholder === '';
  const filteredOptions = emptyOption && !skipEmptyDedup
    ? options.filter((o) => String(o.value) !== '')
    : options;

  const resolvedPlaceholder =
    placeholder !== undefined && placeholder !== null
      ? placeholder
      : emptyOption?.label
      ? emptyOption.label
      : resolveSelectPlaceholder({ label, name });

  const emptyOptionIsHintOnly = isNonSelectableSelectPlaceholder(resolvedPlaceholder);
  const showingHint = emptyOptionIsHintOnly && isEmptySelectValue(value);

  const handleChange = (e) => {
    if (emptyOptionIsHintOnly && e.target.value === '') {
      return;
    }
    onChange?.(e);
  };

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label htmlFor={selectId} className="block text-sm font-bold text-gray-700 mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="relative group">
        <select
          id={selectId}
          name={name}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          required={required}
          className={`w-full px-4 py-3 pr-12 border-2 rounded-xl transition-all duration-300 appearance-none
            ${error
              ? 'border-red-500 focus:border-red-600 focus:ring-4 focus:ring-red-500/20'
              : 'border-gray-200 hover:border-gray-300 focus:border-red-500 focus:ring-4 focus:ring-red-500/20'
            }
            ${disabled
              ? 'bg-gray-100 cursor-not-allowed text-gray-500 font-medium'
              : showingHint
              ? 'bg-white text-black font-bold'
              : 'bg-white text-gray-900 font-medium'
            }
            focus:outline-none shadow-sm hover:shadow-md focus:shadow-lg cursor-pointer`}
          {...props}
        >
          {resolvedPlaceholder && (
            <option value="" {...hintOptionProps(resolvedPlaceholder)}>
              {resolvedPlaceholder}
            </option>
          )}
          {filteredOptions.map((option, index) => (
            <option
              key={`${option.value}-${index}`}
              value={option.value}
              {...hintOptionProps(option.label)}
            >
              {option.label}
            </option>
          ))}
          {Children.map(children, enhanceOptionChild)}
        </select>
        <div
          className={`absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none transition-colors ${
            error ? 'text-red-500' : 'text-gray-400 group-focus-within:text-red-500'
          }`}
        >
          <ChevronDown className="w-5 h-5" />
        </div>
      </div>
      {error && (
        <p className="mt-2 text-sm text-red-600 font-medium flex items-center gap-1">
          <span className="inline-block w-1 h-1 bg-red-600 rounded-full"></span>
          {error}
        </p>
      )}
    </div>
  );
};

export default Select;
